import React, { useCallback } from "react";
import { View, StyleSheet, type ImageSourcePropType } from "react-native";
import { SquareOptionButton } from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useBasicInfo } from "../BasicInfoContext";
import { useNavigation } from "@react-navigation/native";
import { BasicInfoQuestionLayout } from "../components";
import { EMBER } from "@louise/moon-ui/theme/tokens";

const femaleIllustration =
  require("@src/apps/moon/assets/images/medical-history/venus-symbol.png") as ImageSourcePropType;
const maleIllustration =
  require("@src/apps/moon/assets/images/medical-history/mars-symbol.png") as ImageSourcePropType;

export function SexAtBirthScreen() {
  const t = useTranslations();
  const { state, updateState, next } = useBasicInfo("SexAtBirth");
  const navigation = useNavigation();

  const selectedSex = state.sex;

  // A first answer is unambiguous, so move forward immediately. Once the
  // answer exists, keep the screen editable without moving the user away from
  // it when they revisit it.
  const handleSelect = useCallback(
    (id: string) => {
      const sex = id as "AFAB" | "AMAB";
      if (selectedSex == null) {
        next({ sex });
        return;
      }
      updateState({ sex });
    },
    [next, selectedSex, updateState],
  );

  const sexOptions = [
    {
      id: "AFAB",
      illustration: femaleIllustration,
      label: t("med_history.basic_info.sex_at_birth.female"),
    },
    {
      id: "AMAB",
      illustration: maleIllustration,
      label: t("med_history.basic_info.sex_at_birth.male"),
    },
  ];

  return (
    <BasicInfoQuestionLayout
      route="SexAtBirth"
      title={t("med_history.basic_info.sex_at_birth.title")}
      subtitle={t("med_history.basic_info.sex_at_birth.subtitle")}
      helpText={t("med_history.basic_info.help.sex_at_birth")}
      onBack={() => navigation.goBack()}
      onSave={() => next()}
      saveDisabled={selectedSex == null}
      hideSaveButton={selectedSex == null}
      saveLabel={t("med_history.shared.next")}
    >
      <View style={styles.optionsContainer}>
        {sexOptions.map((option) => {
          const isSelected = selectedSex === option.id;
          return (
            <SquareOptionButton
              key={option.id}
              illustration={option.illustration}
              label={option.label}
              isSelected={isSelected}
              onPress={() => handleSelect(option.id)}
              illustrationSize={64}
              labelColor={EMBER.colors.textLight}
              labelSize="lg"
            />
          );
        })}
      </View>
    </BasicInfoQuestionLayout>
  );
}

const styles = StyleSheet.create({
  optionsContainer: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 40,
    width: "100%",
  },
});

export default SexAtBirthScreen;
